/***
*
*   ADD-ONS EDITOR
*   Select and configure add-ons for a plan
*   Links add-ons to plan features via feature_key
*
**********/

import { useState, useEffect } from 'react';
import { Icon } from 'components/lib';
import Style from './bundlePackaging.tailwind.js';

const RESET_INTERVAL_OPTIONS = [
  { value: 'lifetime', label: 'Lifetime' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'concurrent', label: 'Concurrent' }
];

export function AddOnsEditor({ selectedAddOns, availableAddOns, planFeatures, onChange }) {
  const [localSelectedAddOns, setLocalSelectedAddOns] = useState([]);
  const [addOnConfigs, setAddOnConfigs] = useState({});

  useEffect(() => {
    setLocalSelectedAddOns(selectedAddOns || []);
    
    // Initialize configs from plan features for linked add-ons
    const configs = {};
    if (availableAddOns && planFeatures) {
      Object.entries(availableAddOns).forEach(([key, addOn]) => {
        if (selectedAddOns?.includes(key) && addOn.feature_key && planFeatures[addOn.feature_key]) {
          const feature = planFeatures[addOn.feature_key];
          configs[key] = {
            limit: feature.limit,
            resetInterval: feature.resetInterval?.type || 'lifetime'
          };
        }
      });
    }
    setAddOnConfigs(configs);
  }, [selectedAddOns, availableAddOns, planFeatures]);

  const handleToggleAddOn = (addOnKey) => {
    const addOn = availableAddOns[addOnKey];
    let newSelectedAddOns;
    let newFeatures = { ...planFeatures };

    if (localSelectedAddOns.includes(addOnKey)) {
      // Remove add-on
      newSelectedAddOns = localSelectedAddOns.filter(k => k !== addOnKey);
      
      // Remove config
      const newConfigs = { ...addOnConfigs };
      delete newConfigs[addOnKey];
      setAddOnConfigs(newConfigs);
    } else {
      // Add add-on
      newSelectedAddOns = [...localSelectedAddOns, addOnKey];
      
      // If add-on has feature_key, add/update the feature in plan
      if (addOn.feature_key) {
        // Initialize config
        setAddOnConfigs(prev => ({
          ...prev,
          [addOnKey]: {
            limit: addOn.per_unit || null,
            resetInterval: 'lifetime'
          }
        }));

        // Add or update feature
        if (!newFeatures[addOn.feature_key]) {
          newFeatures[addOn.feature_key] = {
            feature_key: addOn.feature_key,
            name: addOn.name,
            enabled: true,
            limit: addOn.per_unit || null,
            unit: addOn.unit || null,
            resetInterval: { type: 'lifetime' }
          };
        }
      }
    }

    setLocalSelectedAddOns(newSelectedAddOns);
    onChange(newSelectedAddOns, newFeatures);
  };

  const handleConfigChange = (addOnKey, field, value) => {
    const addOn = availableAddOns[addOnKey];
    
    // Update local config
    const newConfigs = {
      ...addOnConfigs,
      [addOnKey]: {
        ...addOnConfigs[addOnKey],
        [field]: value
      }
    };
    setAddOnConfigs(newConfigs);

    // Update plan features if add-on has feature_key
    if (addOn.feature_key) {
      const newFeatures = { ...planFeatures };
      
      if (newFeatures[addOn.feature_key]) {
        newFeatures[addOn.feature_key] = {
          ...newFeatures[addOn.feature_key],
          limit: field === 'limit' ? (value ? parseInt(value) : null) : newConfigs[addOnKey].limit,
          resetInterval: field === 'resetInterval' ? { type: value } : newFeatures[addOn.feature_key].resetInterval
        };
      } else {
        newFeatures[addOn.feature_key] = {
          feature_key: addOn.feature_key,
          name: addOn.name,
          enabled: true,
          limit: field === 'limit' ? (value ? parseInt(value) : null) : null,
          unit: addOn.unit || null,
          resetInterval: field === 'resetInterval' ? { type: value } : { type: 'lifetime' }
        };
      }

      onChange(localSelectedAddOns, newFeatures);
    }
  };

  const addOnKeys = Object.keys(availableAddOns || {});

  const getPricingLabel = (addOn) => {
    if (addOn.type === 'one_time') {
      return addOn.price_min ? `From $${addOn.price_min}` : `$${addOn.price}`;
    }
    if (addOn.pricing_type === 'per_unit' || addOn.pricing_type === 'per_user') {
      return `$${addOn.price}/${addOn.per_unit || 1} ${addOn.unit || 'unit'}`;
    }
    return `$${addOn.price}/${addOn.interval}`;
  };

  return (
    <div className={Style.featureEditor}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-medium text-slate-700">
          Available Add-ons ({localSelectedAddOns.length} selected)
        </h3>
      </div>

      {addOnKeys.length === 0 ? (
        <div className={Style.emptyState}>
          <p className={Style.emptyStateText}>No add-ons available. Create add-ons in the Add-ons tab first.</p>
        </div>
      ) : (
        <div className={Style.modulesList}>
          {addOnKeys.map((key) => {
            const addOn = availableAddOns[key];
            const isSelected = localSelectedAddOns.includes(key);
            const config = addOnConfigs[key] || {};
            const hasFeatureLink = !!addOn.feature_key;
            const linkedFeature = hasFeatureLink ? planFeatures?.[addOn.feature_key] : null;

            return (
              <div key={key} className={`${Style.moduleItem} ${isSelected ? 'bg-green-50 border border-green-200' : ''}`}>
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={() => handleToggleAddOn(key)}
                    className={`w-5 h-5 mt-1 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                      isSelected
                        ? 'bg-green-500 border-green-500'
                        : 'border-slate-300 hover:border-slate-400'
                    }`}
                  >
                    {isSelected && <Icon image="check" color="white" size={12} />}
                  </button>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={Style.moduleName}>{addOn.name}</span>
                      <span className="text-xs text-green-600 font-medium">{getPricingLabel(addOn)}</span>
                    </div>
                    <span className={Style.moduleKey}>{key}</span>
                    {addOn.description && (
                      <p className="text-xs text-slate-500 mt-1">{addOn.description}</p>
                    )}
                    {hasFeatureLink && (
                      <div className="flex items-center gap-1 mt-1">
                        <Icon image="link" color="blue" size={12} />
                        <span className="text-xs text-blue-600">Links to: {addOn.feature_key}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Config fields when add-on is selected and has feature link */}
                {isSelected && hasFeatureLink && (linkedFeature?.limit !== undefined || addOn.per_unit) && (
                  <div className="flex items-center gap-3 flex-shrink-0 mt-2 md:mt-0">
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-slate-500">Base Limit:</label>
                      <input
                        type="number"
                        className={Style.featureLimitInput}
                        value={config.limit ?? linkedFeature?.limit ?? ''}
                        onChange={(e) => handleConfigChange(key, 'limit', e.target.value)}
                        placeholder="∞"
                        min="0"
                      />
                      {addOn.unit && (
                        <span className="text-xs text-slate-500">{addOn.unit}</span>
                      )}
                    </div>

                    <select
                      className={Style.featureIntervalSelect}
                      value={config.resetInterval || linkedFeature?.resetInterval?.type || 'lifetime'}
                      onChange={(e) => handleConfigChange(key, 'resetInterval', e.target.value)}
                    >
                      {RESET_INTERVAL_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {localSelectedAddOns.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700">
            <Icon image="info" color="blue" size={14} className="inline mr-1" />
            Selected add-ons will be available for purchase by organizations on this plan.
            Add-ons with feature links will update the corresponding feature limits when purchased.
          </p>
        </div>
      )}
    </div>
  );
}